/**
 * ClassRoster.tsx
 * Manage class roster: create class, add/remove students, award stars.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Star, Trash2, Users, ChevronLeft } from 'lucide-react';
import Avatar from '@components/shared/Avatar';
import Button from '@components/shared/Button';
import { useAppStore } from '@store/appStore';
import { useClassStore } from '@store/classStore';
import { addStudent, removeStudent, createClass, getClassesByTeacher } from '@db/studentService';
import { useStarAward } from '@hooks/useStarAward';
import { useEntitlements } from '@utils/entitlements';
import type { AvatarEmoji, ClassRoom } from '@types-local/class.types';

const AVATARS: AvatarEmoji[] = ['🦁', '🐯', '🦊', '🐧', '🦅', '🐬', '🦋', '🐸'];
const CLASS_COLORS = ['#5660F7', '#FF820A', '#22C55E', '#A855F7', '#3B82F6', '#EF4444'];

export default function ClassRoster(): React.ReactElement {
  const { roster } = useClassStore();
  const { activeClassId, setActiveClassId, teacherUid } = useAppStore((s) => ({
    activeClassId: s.activeClassId,
    setActiveClassId: s.setActiveClassId,
    teacherUid: s.teacherUid,
  }));
  const { grantStars } = useStarAward();
  const { maxClasses, maxStudentsPerClass } = useEntitlements();

  const [newStudentName, setNewStudentName] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarEmoji>('🦁');
  const [addingStudent, setAddingStudent] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [creatingClass, setCreatingClass] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [showCreateClass, setShowCreateClass] = useState(false);

  const [myClasses, setMyClasses] = useState<ClassRoom[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [error, setError] = useState('');

  // Load the teacher's saved classes (stored in gurohubpptapp, keyed by the
  // logged-in teacher UID) so they can pick one instead of re-creating it each
  // session. Auto-selects when there's exactly one.
  useEffect(() => {
    if (!teacherUid) return;
    let cancelled = false;
    setLoadingClasses(true);
    getClassesByTeacher(teacherUid)
      .then((classes) => {
        if (cancelled) return;
        setMyClasses(classes);
        if (!activeClassId && classes.length === 1) setActiveClassId(classes[0].id);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[GuroHub] Failed to load classes:', err);
        setError(err instanceof Error ? err.message : 'Could not load your classes.');
      })
      .finally(() => { if (!cancelled) setLoadingClasses(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherUid]);

  const classLimitReached = myClasses.length >= maxClasses;
  const studentLimitReached = roster.length >= maxStudentsPerClass;

  const handleCreateClass = async () => {
    if (!newClassName.trim() || classLimitReached) return;
    setError('');
    setCreatingClass(true);
    try {
      const color = CLASS_COLORS[Math.floor(Math.random() * CLASS_COLORS.length)];
      const classId = await createClass(newClassName.trim(), color);
      setActiveClassId(classId);
      setNewClassName('');
      setShowCreateClass(false);
    } catch (err) {
      console.error('[GuroHub] Create class failed:', err);
      setError(err instanceof Error ? err.message : 'Could not create the class. Please try again.');
    } finally {
      setCreatingClass(false);
    }
  };

  const handleAddStudent = async () => {
    if (!newStudentName.trim() || !activeClassId || studentLimitReached) return;
    setError('');
    setAddingStudent(true);
    try {
      await addStudent(activeClassId, newStudentName.trim(), selectedAvatar);
      setNewStudentName('');
      setShowAddForm(false);
    } catch (err) {
      console.error('[GuroHub] Add student failed:', err);
      setError(err instanceof Error ? err.message : 'Could not add the student. Please try again.');
    } finally {
      setAddingStudent(false);
    }
  };

  // No class selected — show saved classes to pick from, or create one
  if (!activeClassId) {
    return (
      <div className="p-4 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center"><Users size={16} className="text-blue-400" /></div>
          <h2 className="text-sm font-bold text-slate-900">My Classes</h2>
        </div>

        {loadingClasses ? (
          <p className="text-xs text-slate-500 text-center py-6">Loading your classes…</p>
        ) : myClasses.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">Pick a class to manage or present with:</p>
            {myClasses.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveClassId(c.id)}
                className="w-full flex items-center gap-3 px-3 py-3 bg-surface-elevated border border-surface-border rounded-xl hover:border-brand-500/40 transition-colors text-left"
              >
                <span className="w-8 h-8 rounded-lg flex-shrink-0" style={{ backgroundColor: c.coverColor }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{c.name}</p>
                  <p className="text-[10px] text-slate-500">{c.studentCount ?? 0} students</p>
                </div>
              </button>
            ))}
            {classLimitReached ? (
              <p className="text-[11px] text-center text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2">
                You've reached your plan's class limit ({maxClasses}). Upgrade to add more.
              </p>
            ) : (
              <Button fullWidth variant="ghost" icon={<Plus size={13} />} onClick={() => setShowCreateClass(true)}>
                New Class
              </Button>
            )}
          </div>
        ) : (
          <div className="rounded-2xl bg-surface-elevated border border-surface-border p-5 text-center space-y-3">
            <p className="text-3xl">🏫</p>
            <p className="text-sm text-slate-700 font-medium">No class created yet</p>
            <p className="text-xs text-slate-500">Create your first class to manage your roster</p>
            <Button fullWidth onClick={() => setShowCreateClass(true)}>Create My Class</Button>
          </div>
        )}

        <AnimatePresence>
          {showCreateClass && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
              <input type="text" value={newClassName} onChange={(e) => setNewClassName(e.target.value)}
                placeholder="e.g. Grade 7 — Section Mabini"
                className="w-full bg-surface-elevated border border-surface-border rounded-xl px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-brand-500" />
              <Button fullWidth onClick={handleCreateClass} loading={creatingClass} disabled={!newClassName.trim() || classLimitReached}>
                Create Class
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {error && (
          <p className="text-xs text-red-500 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">{error}</p>
        )}
      </div>
    );
  }

  const activeClass = myClasses.find((c) => c.id === activeClassId);

  return (
    <div className="p-4 space-y-4">
      {/* Back to the class list so the teacher isn't stranded in one class */}
      <button
        onClick={() => setActiveClassId(null)}
        className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-brand-600 transition-colors"
      >
        <ChevronLeft size={13} /> All classes
      </button>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: activeClass?.coverColor ?? '#3B82F6' }}>
            <Users size={16} className="text-white" />
          </span>
          <div>
            <h2 className="text-sm font-bold text-slate-900">{activeClass?.name ?? 'My Class'}</h2>
            <p className="text-[10px] text-slate-500">{roster.length} students</p>
          </div>
        </div>
        <Button size="sm" variant="ghost" icon={<Plus size={13} />} onClick={() => setShowAddForm((v) => !v)} disabled={studentLimitReached}>
          Add
        </Button>
      </div>
      {studentLimitReached && (
        <p className="text-[11px] text-center text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2">
          Student limit reached ({maxStudentsPerClass}). Upgrade your plan to add more.
        </p>
      )}
      {error && (
        <p className="text-xs text-red-500 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">{error}</p>
      )}

      {/* Add student form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="bg-surface-elevated border border-surface-border rounded-xl p-3 space-y-3 overflow-hidden">
            <input type="text" value={newStudentName} onChange={(e) => setNewStudentName(e.target.value)}
              placeholder="Student name"
              className="w-full bg-surface border border-surface-border rounded-xl px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-brand-500" />
            <div className="flex gap-1.5 flex-wrap">
              {AVATARS.map((a) => (
                <button key={a} onClick={() => setSelectedAvatar(a)}
                  className={`text-lg p-1.5 rounded-lg transition-all ${selectedAvatar === a ? 'bg-brand-500/30 border border-brand-500/50' : 'hover:bg-surface'}`}>
                  {a}
                </button>
              ))}
            </div>
            <Button fullWidth size="sm" onClick={handleAddStudent} loading={addingStudent} disabled={!newStudentName.trim()}>
              Add Student
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Roster list */}
      {roster.length === 0 ? (
        <p className="text-center text-xs text-slate-500 py-8">No students yet. Add some!</p>
      ) : (
        <div className="space-y-2">
          {roster.map((student, i) => (
            <motion.div key={student.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="flex items-center gap-2.5 px-3 py-2.5 bg-surface-elevated border border-surface-border rounded-xl hover:border-brand-500/30 transition-colors group">
              <span className="text-xs text-slate-400 w-4 font-mono">{i + 1}</span>
              <Avatar emoji={student.avatar} name={student.name} size="sm" level={student.level} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-900 truncate">{student.name}</p>
                <p className="text-[10px] text-slate-500">⭐ {student.totalStars} · {student.xp} XP</p>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => grantStars(student.id, student.name, 1)}
                  className="p-1.5 rounded-lg text-yellow-500 hover:bg-yellow-500/10 transition-colors" title="Award 1 star">
                  <Star size={13} />
                </button>
                <button onClick={() => activeClassId && removeStudent(activeClassId, student.id)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-400/10 transition-colors">
                  <Trash2 size={13} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
